// @flow

import _ from 'lodash';
import { v4 } from 'uuid';

import { RSAA } from 'redux-api-middleware';
import { store } from 'src/stores/store';
import { getShop } from 'src/helpers';

const apiConfig = require('src/services/api.config.json');

const SIP_V3_URL = process.env.REACT_APP_SIP_V3_BASE_URL || 'http://localhost';
const ADMIN_V2_URL = process.env.REACT_APP_ADMIN_V2_BASE_URL || 'http://localhost';
const ADMIN_V3_URL = process.env.REACT_APP_ADMIN_V3_BASE_URL || 'http://localhost';
const FILTER_URL = process.env.REACT_APP_FILTER_URL || 'http://localhost';
const WIDGET_INTEGRATION_URL =
  process.env.REACT_APP_WIDGET_INTEGRATION_BASE_URL || 'http://localhost';

const SESSION_ID_KEY = 'Boost-Sd-Session-Id';
const ADMIN_USER_ID_KEY = 'Boost-Sd-User-Id';
const SESSION_ID_TTL_KEY = 'Boost-Sd-Session-Ttl';
const SESSION_ID_TTL = 1000 * 60 * 60 * 24; // 1 day in milliseconds

class ApiService {
  /**
   * Session ID
   * @type {string}
   */
  constructor() {
    this.sessionId = null;
    this.sessionIdTtl = null;
    this.inMemoryStorage = { [SESSION_ID_KEY]: null, [SESSION_ID_TTL_KEY]: null };
  }

  /**
   *
   * @param {string} key
   * @returns
   */
  getItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return this.inMemoryStorage[key] || null;
    }
  }

  /**
   *
   * @param {string} key
   * @param {} value
   */
  setItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      this.inMemoryStorage[key] = value;
    }
  }

  /**
   * Generate session ID
   * @returns {string}
   */
  initSessionId() {
    const currentTime = new Date().getTime();
    const storedSessionId = this.getItem(SESSION_ID_KEY);
    const storedTTL = Number(this.getItem(SESSION_ID_TTL_KEY) || 0);
    const nextTTL = currentTime + SESSION_ID_TTL;

    if (storedSessionId && storedTTL && currentTime < storedTTL) {
      this.sessionId = storedSessionId;
      this.setItem(SESSION_ID_TTL_KEY, nextTTL);
    } else {
      this.sessionId = v4();
      this.setItem(SESSION_ID_KEY, this.sessionId);
      this.setItem(SESSION_ID_TTL_KEY, nextTTL);
    }
    console.log(SESSION_ID_KEY, this.sessionId, '| Expired:', new Date(nextTTL));
    return this.sessionId;
  }

  getSessionId() {
    return this.sessionId || this.initSessionId();
  }

  setUserId(userId) {
    const shop = getShop();
    const key = `${shop}-${ADMIN_USER_ID_KEY}`;
    this.setItem(key, userId);
  }

  getUserId() {
    const shop = getShop();
    const key = `${shop}-${ADMIN_USER_ID_KEY}`;
    return this.getItem(key) || '';
  }

  /**
   * call the API with redux-api-middleware
   *
   * @param  {string} method    request method
   * @param  {string} path
   * @param  {object} payload   request payload to feed to the request
   *                            {
   *                              body: {},
   *                              params: {},
   *                              queries: {},
   *                            }
   * @param  {array} types      list of action type request
   *                            [
   *                              request action handler,
   *                              success action handler,
   *                              failure action handler
   *                            ]
   *
   * @return {object}           The RSAA object for API middleware
   */
  sendRequest(
    route: string,
    payload: Object = {},
    types: Array<string | Object> = ['REQUEST', 'SUCCESS', 'FAILURE']
  ) {
    const { attachments, auth0AccessToken } = payload;
    const sendToFilterApi = _.startsWith(route, 'filter');

    // Send request with Auth0 token
    if (auth0AccessToken) {
      return {
        [RSAA]: this.buildRequestPayloadWithAuth0Token(route, payload, types)
      };
    }

    // Send request to Filter API
    if (sendToFilterApi) {
      return {
        [RSAA]: this.buildRequestPayloadToFilterAPI(route, payload, types)
      };
    }

    const sendToSearchApi = _.startsWith(route, 'search');
    // Send request to Filter API
    if (sendToSearchApi) {
      return {
        [RSAA]: this.buildRequestPayloadToSearchAPI(route, payload, types)
      };
    }

    const metaData = this.buildRequestPayload(route, payload, types);

    if (
      route.includes('admin-v3.analytics-v2') &&
      route !== 'admin-v3.analytics-v2.get-collections'
    ) {
      if (!window.abortApiAnalyticController) {
        window.abortApiAnalyticController = new AbortController();
      }
      metaData.options = { signal: window.abortApiAnalyticController.signal };
    }

    return {
      [RSAA]: attachments
        ? this.buildRequestPayloadWithAttachements(route, payload, types)
        : metaData
    };
  }

  /**
   *
   * @param {*} route
   * @param {*} payload
   * @param {*} type // type = json | file
   * @returns
   */
  async fetchApi(route: string, payload: Object = {}, resType: string = 'data') {
    const { attachments, body = {} } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { method, path, type } = routeConfig;

    const currentState = store.getState();
    const { authToken } = currentState.oauth;

    const endpoint = this.getUrl(path, payload, type);
    // Init request payload
    let requestPayload: Object = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken,
        [ADMIN_USER_ID_KEY]: this.getUserId()
      }
    };

    // Build request body
    if (['post', 'put', 'patch', 'delete'].includes(method) && body) {
      if (attachments) {
        let data = new FormData();
        attachments.forEach(file => {
          data.append('files', file);
        });
        data.append('body', JSON.stringify(body));
        requestPayload.body = data;
        requestPayload.headers = {
          'x-bc-sf-filter-auth': authToken,
          [ADMIN_USER_ID_KEY]: this.getUserId()
        };
      } else requestPayload.body = JSON.stringify(body);
    }

    const response = await fetch(endpoint, requestPayload);

    if (attachments || resType === 'file') {
      return { response, status: response.status };
    }
    return { response: await response.json(), status: response.status }; // Parse the JSON response  }
  }

  /**
   * Locate api config by given route
   *
   * @param  {string} route   management.sync.force-sync
   *
   * @return {object}       api config
   */
  getApiConfig(route: string) {
    const parts = route.split('.');
    const routeType = parts[0];
    const routeName = parts.pop();
    const routeGroup = parts.join('.');
    // Get route group that api belongs to
    const routes = _.get(apiConfig, routeGroup);

    // Get api details
    const api = _.find(routes, { name: routeName });

    return { ...api, type: routeType };
  }

  /**
   * build main URL for the requested service
   * @param  {string} type
   * @return {string} the main URL
   */
  getMainUrl(type: string): string {
    switch (type) {
      case 'admin-v2':
        return ADMIN_V2_URL;
      case 'sip-v3':
        return SIP_V3_URL;
      case 'admin-v3':
        return ADMIN_V3_URL;
      case 'shopify-integration':
        return WIDGET_INTEGRATION_URL;
      case 'filter':
      case 'search':
        return FILTER_URL;
      default:
        return SIP_V3_URL;
    }
  }

  /**
   * build the endpoint URL for the requested service
   * @param  {string} path
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @return {string}           the URL has been built
   */
  getUrl(path: string, payload: Object = {}, type: string) {
    let url = `${this.getMainUrl(type)}${path}`;
    const { params, queries } = payload;

    // Inject params
    if (!_.isEmpty(params)) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, String(value));
      });
    }

    // Inject queries
    if (!_.isEmpty(queries)) {
      const queryArr = Object.entries(queries).map(([key, value]) => {
        let query = `${key}=${String(value)}`;
        if (Array.isArray(value)) {
          // Set param for each element for the same key
          const temp = value.map(item => {
            return `${key}=${String(item)}`;
          });

          query = temp.join('&');
        } else {
          if (key === 'q' && type === 'search') {
            query = new URLSearchParams();
            query.append(key, value);
          } else query = `${key}=${String(value)}`;
        }

        return query;
      });
      url = `${url}?${queryArr.join('&')}`;
    }

    return url;
  }

  /**
   * Build request payload
   * @param  {string} route string route API
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @param  {Array<string | Object} types string route API
   * @return {Object} requestPayload
   */
  buildRequestPayload(route: string, payload: Object, types: Array<string | Object>) {
    const { body } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { method, path, type } = routeConfig;

    const currentState = store.getState();
    const { authToken } = currentState.oauth;

    // Init request payload
    let requestPayload: Object = {
      endpoint: this.getUrl(path, payload, type),
      method: routeConfig.method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken,
        [SESSION_ID_KEY]: this.getSessionId(),
        [ADMIN_USER_ID_KEY]: this.getUserId()
      },
      types
    };

    // Build request body
    if (['post', 'put', 'patch', 'delete'].includes(method) && body) {
      requestPayload.body = JSON.stringify(body);
    }

    return requestPayload;
  }

  /**
   * Build request payload with Attachements token
   * @param  {string} route string route API
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @param  {Array<string | Object} types string route API
   * @return {Object} requestPayload
   */
  buildRequestPayloadWithAttachements(
    route: string,
    payload: Object,
    types: Array<string | Object>
  ) {
    const { attachments, body = {} } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { path, type } = routeConfig;

    const currentState = store.getState();
    const { authToken } = currentState.oauth;

    let data = new FormData();
    attachments.forEach(file => {
      data.append('files', file);
    });
    data.append('body', JSON.stringify(body));

    // Init request payload
    let requestPayload: Object = {
      endpoint: this.getUrl(path, payload, type),
      method: routeConfig.method.toUpperCase(),
      headers: {
        // Without content type
        // 'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
        'x-bc-sf-filter-auth': authToken,
        [ADMIN_USER_ID_KEY]: this.getUserId()
      },
      types
    };

    // Send form data
    requestPayload.body = data;

    return requestPayload;
  }

  /**
   * Build request payload with Auth0 token
   * @param  {string} route string route API
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @param  {Array<string | Object} types string route API
   * @return {Object} requestPayload
   */
  buildRequestPayloadWithAuth0Token(route: string, payload: Object, types: Array<string | Object>) {
    const { body, auth0AccessToken } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { method, path, type } = routeConfig;

    // Init request payload
    let requestPayload: Object = {
      endpoint: this.getUrl(path, payload, type),
      method: routeConfig.method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth0AccessToken}`
      },
      types
    };

    // Build request body
    if (['post', 'put', 'patch', 'delete'].includes(method) && body) {
      requestPayload.body = JSON.stringify(body);
    }

    return requestPayload;
  }

  /**
   * Build request payload to Filter API
   * @param  {string} route string route API
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @param  {Array<string | Object} types string route API
   * @return {Object} requestPayload
   */
  buildRequestPayloadToFilterAPI(route: string, payload: Object, types: Array<string | Object>) {
    const { body } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { method, path, type } = routeConfig;

    // Init request payload
    let requestPayload: Object = {
      endpoint: this.getUrl(path, payload, type),
      method: routeConfig.method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json'
      },
      types
    };

    // Build request body
    if (['post', 'put', 'patch', 'delete'].includes(method) && body) {
      requestPayload.body = JSON.stringify(body);
    }

    return requestPayload;
  }

  /**
   * Build request payload to Search API
   * @param  {string} route string route API
   * @param  {object} payload   the request payload to feed to the request
   *                          {
   *                            body: {},
   *                            params: {},
   *                            queries: {},
   *                          }
   * @param  {Array<string | Object} types string route API
   * @return {Object} requestPayload
   */
  buildRequestPayloadToSearchAPI(route: string, payload: Object, types: Array<string | Object>) {
    const { body } = payload;

    // Get config for requested route
    const routeConfig = this.getApiConfig(route) || {};

    const { method, path, type } = routeConfig;

    const currentState = store.getState();
    const { authToken } = currentState.oauth;
    // Init request payload
    let requestPayload: Object = {
      endpoint: this.getUrl(path, payload, type),
      method: routeConfig.method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        'x-bc-sf-filter-auth': authToken
      },
      types
    };

    // Build request body
    if (['post', 'put', 'patch', 'delete'].includes(method) && body) {
      requestPayload.body = JSON.stringify(body);
    }

    return requestPayload;
  }
}

const apiService = new ApiService();
export default apiService;
